import React from 'react';
import {Divider, Grid, IconButton, Typography} from '@mui/material';
import { currencyFormatter } from '../utils/currencyFormat';
import { DEFAULT_CURRENCY_CODE } from '../constants/defaults';
import formatCurrencyWords from '../utils/formatCurrencyWords';
import EditInvoicePrePaymentButton from "./EditInvoicePrePaymentButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";

// Reusable SummaryRow component
const SummaryRow = ({
  label,
  value,
  currencyCode,
  testId,
  isBold = false,
  color = 'inherit',
  prefix = '',
  suffix = '',
  hideIfZero = false,
  readOnly = false,
  onEdit = null,
  onDelete = null,
  editComponent: EditComponent = null,
}) => {
  if (hideIfZero && value === 0) return null;
  return (
    <>
        <Grid size={6} display={'flex'} sx={{ alignItems: 'center' }}>
            <Typography>{label}</Typography>
            {!readOnly && (onEdit || onDelete || EditComponent) && (
                <>
                    {EditComponent ? (
                        <EditComponent />
                    ) : (
                        onEdit && (
                            <IconButton aria-label="edit" onClick={onEdit} color="primary">
                                <EditIcon />
                            </IconButton>
                        )
                    )}

                    {onDelete && (
                        <IconButton aria-label="delete" onClick={onDelete} color="error">
                            <DeleteIcon />
                        </IconButton>
                    )}
                </>
            )}
        </Grid>
      <Grid size={2}>
        <Typography fontWeight={isBold ? 'bold' : 'normal'}>{currencyCode}</Typography>
      </Grid>
      <Grid size={4} style={{ textAlign: 'right' }}>
        <Typography data-testid={testId} variant="body1" color={color} fontWeight={isBold ? 'bold' : 'normal'}>
          {prefix}
          {currencyFormatter().format(value)}
          {suffix}
        </Typography>
      </Grid>
    </>
  );
};

const DocumentSummarySection = ({
  itemsSubtotal,
  subtotal,
  totalItemAllowances,
  totalItemCharges,
  totalExcludingTax,
  totalIncludingTax,
  totalTaxAmount,
  taxInOMR,
  totalPayableAmount,
  documentCurrencyCode,

  headerCharges = 0,
  headerAllowance = 0,

  totalTaxableAmount = 0,
  totalNonTaxableAmount = 0,
  taxBreakdown = {},
  payableRoundingAmount,
  prepaidAmount,
  readOnly=false,
  addPrepaidAmount,
  addPrepaymentDetails,
    getValues
}) => {
  const currencyCode = documentCurrencyCode;

  const { allowances: documentAllowanceSummary, charges: documentChargeSummary } = taxBreakdown;
  return (
      <Grid container spacing={1.5}>
      <SummaryRow
        label="Item Subtotal"
        value={itemsSubtotal}
        currencyCode={documentCurrencyCode}
        testId="items-subtotal-value"
      />

      <SummaryRow
        label="Total Item Allowance"
        value={totalItemAllowances}
        currencyCode={currencyCode}
        testId="total-item-discount-value"
        color="error"
        prefix="-"
        hideIfZero
      />

      <SummaryRow
        label="Total Item Charge"
        value={totalItemCharges}
        currencyCode={currencyCode}
        testId="total-item-charges-value"
        hideIfZero
      />

      <SummaryRow
        label="Items Net Total"
        value={subtotal}
        currencyCode={currencyCode}
        testId="total-items-net-value"
        isBold={true}
      />

      <Grid size={12}>
        <Divider />
      </Grid>

      {/* Document-level Allowances by Tax Category */}
      {Object.keys(documentAllowanceSummary || {}).map((taxKey) => {
        const breakdown = documentAllowanceSummary[taxKey];
        if (breakdown.taxableAmount > 0) {
          return (
            <SummaryRow
              key={`allowance-${taxKey}`}
              label={`Document-Level Allowance (${breakdown.category} - ${breakdown.rate}%)`}
              value={breakdown.taxableAmount}
              currencyCode={currencyCode}
              testId={`allowance-${taxKey}-value`}
              color="error"
              prefix="-"
            />
          );
        }
        return null;
      })}

      {/* Document-level Charges by Tax Category */}
      {Object.keys(documentChargeSummary || {}).map((taxKey) => {
        const breakdown = documentChargeSummary[taxKey];
        if (breakdown.taxableAmount !== 0) {
          return (
            <SummaryRow
              key={`charge-${taxKey}`}
              label={`Document-Level Charge (${breakdown.category} - ${breakdown.rate}%)`}
              value={breakdown.taxableAmount}
              currencyCode={currencyCode}
              testId={`charge-${taxKey}-value`}
            />
          );
        }
        return null;
      })}

      <SummaryRow
        label="Total Document Allowance"
        value={headerAllowance}
        currencyCode={currencyCode}
        testId="header-allowance-value"
        color="error"
        prefix="-"
        hideIfZero
      />

      <SummaryRow
        label="Total Document Charge"
        value={headerCharges}
        currencyCode={currencyCode}
        testId="header-charge-value"
        hideIfZero
      />

      <SummaryRow
        label="Total Non-taxable Amount"
        value={totalNonTaxableAmount}
        currencyCode={currencyCode}
        testId="total-non-taxable-amount-value"
        hideIfZero
      />

      <SummaryRow
        label="Total Taxable Amount"
        value={totalTaxableAmount}
        currencyCode={currencyCode}
        testId="total-taxable-amount-value"
        hideIfZero
      />

      <SummaryRow
        label="Total Excluding Tax"
        value={totalExcludingTax}
        currencyCode={currencyCode}
        testId="total-excluding-tax-value"
        isBold
      />

      <SummaryRow
        label="Total Tax Amount"
        value={totalTaxAmount}
        currencyCode={currencyCode}
        testId="total-tax-amount-value"
        isBold
      />

      {taxInOMR != null && (
        <SummaryRow
          label={`Total Tax Amount (${DEFAULT_CURRENCY_CODE})`}
          value={taxInOMR}
          currencyCode={DEFAULT_CURRENCY_CODE}
          testId="total-tax-omr-value"
          isBold
        />
      )}

      <SummaryRow
        label="Total Including Tax"
        value={totalIncludingTax}
        currencyCode={currencyCode}
        testId="total-including-tax-value"
        isBold
      />

      {payableRoundingAmount != null && payableRoundingAmount !== 0 && (
        <SummaryRow
          label="Payable Rounding Amount"
          value={payableRoundingAmount}
          currencyCode={currencyCode}
          testId="payable-rounding-amount-value"
          isBold
        />
      )}

      <Grid size={12}>
        <Divider />
      </Grid>

      {prepaidAmount != null && prepaidAmount !== 0 && (
          <SummaryRow
          label="Prepaid Amount"
          value={prepaidAmount}
          currencyCode={currencyCode}
          testId="prepaid-amount-value"
          isBold
          readOnly={readOnly}
          onDelete={
            !readOnly && addPrepaidAmount
              ? () => {
                  addPrepaidAmount(null);
                  addPrepaymentDetails?.(null);
                }
              : undefined
          }
          editComponent={
            !readOnly && addPrepaidAmount
              ? () => {
                  const ref = getValues?.('prepayment_invoice_reference') || {};
                  return (
                    <EditInvoicePrePaymentButton
                      addPrepaidAmount={addPrepaidAmount}
                      addPrepaymentDetails={addPrepaymentDetails}
                      initialValue={{
                        prepaid_amount: prepaidAmount ?? '',
                        invoice_number: ref.invoice_number ?? '',
                        invoice_uuid: ref.invoice_uuid ?? '',
                        invoice_date: dayjs(ref.invoice_date) ?? null,
                      }}
                      readOnly={readOnly}
                      currentNetAmount={totalIncludingTax}
                    />
                  );
                }
              : undefined
          }
          />
      )}

      <SummaryRow
        label="Grand Total"
        value={totalPayableAmount}
        currencyCode={currencyCode}
        testId="grand-total-value"
        isBold
      />

      <Grid size={12}>
        <Divider />
      </Grid>

      <Grid size={12} style={{ textAlign: 'right' }}>
        <Typography data-testid="currency-words-value" variant="body1">
          {!isNaN(totalPayableAmount) && formatCurrencyWords(totalPayableAmount, currencyCode)}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default DocumentSummarySection;
